import { Box } from "@mui/material";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { FeedType, KnowledgeFeedOrder, QuickFilterDto } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { FeedContainer } from "views/knowledge/feed/components/FeedContainer";
import { IFeedFilterOptions } from "views/knowledge/feed/components/FeedFilter";
import { getFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { FeedManager, FeedManagerHandle } from "views/knowledge/feed/components/FeedManager";
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { useFeedTopicContext } from "views/knowledge/feed/FeedTopic";
import { FeedTopicFilter } from "views/knowledge/feed/FeedTopic/FeedTopicFilter";
import { FeedTopicRightPanel } from "views/knowledge/feed/FeedTopic/FeedTopicRightPanel";
import { OutsideRightPanel } from "views/knowledge/publication/pages/components/OutsideRightPanel";

/**
 * Feed do Tópico.
 * @param props
 */
export const FeedTopicHome: React.FC = () => {
    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();
    const layout = useInsideLayout();
    const { topic, includeDescendants, openRightPanel, setOpenRightPanel, isHome } = useFeedTopicContext();

    const { t } = useTranslation("knowledge");

    const [count, setCount] = useState<number | undefined | null>(undefined);
    const [prevPub, setPrevPub] = useState<string | null>(location.state?.publicationId ?? null);
    const [expandedAdvFilter, setExpandedAdvFilter] = useState<boolean>(location.state?.expanded ?? false);
    const prevExpandedAdvFilter = useRef<boolean>(false);
    const [showPanel, setShowPanel] = useState<boolean>(true);

    const viewFilter = useRef<IFeedFilterOptions>(undefined!);

    ///
    const handleAdvFilterChange = useCallback((filter: IFeedFilterOptions) => {
        viewFilter.current = filter;
        setCount(undefined);
        setPrevPub(null);
    }, [topic]);

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        const filters = getFilter(viewFilter.current);

        var result = await api.v1.knowledgeFeed.getFeedTopic({
            topicId: topic!.topicId,
            startPublicationId: location.state?.publicationId ?? null,
            startIndex,
            endIndex,
            filters,
            order: viewFilter.current.order,
            searchText: viewFilter.current.searchText ?? null,
            includeDescendants: viewFilter.current.topicDescendants ?? false
        });
        setCount(result.data.totalRows);
        return result.data.items;
    };

    ///
    const handleNewItem = (e: FeedCustomEventProps) => {
        if (e.detail == topic?.topicId && viewFilter.current.searchText == "" && viewFilter.current.advFilters.length == 0 && viewFilter.current.order == KnowledgeFeedOrder.Latest && !feedRef.current.scrolled())
            feedRef.current.reset();
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useDidUpdateEffect(() => {
        viewFilter.current.topicDescendants = includeDescendants;
        setCount(undefined);
        setPrevPub(null);
    }, [includeDescendants]);

    ///
    useDidUpdateEffect(() => {
        prevExpandedAdvFilter.current = expandedAdvFilter;
    }, [expandedAdvFilter]);

    ///
    useEffect(() => {
        if (count == null && prevPub == null) {
            navigate(document.location.search, { replace: true, state: { filter: viewFilter.current, expanded: expandedAdvFilter } });
        }
    }, [count]);

    ///
    useEffect(() => {
        if (prevPub != location.state?.publicationId) {
            setPrevPub(null);
        }
        else if (prevExpandedAdvFilter.current == expandedAdvFilter) {
            setCount(null);
            feedRef.current?.reset();
        }
    }, [location.state]);

    ///
    useEffect(() => {
        layout.changeSubHeaderText(t("Feed.Topic.TopicText"));

        const urlParams = new URLSearchParams(location.search);
        const initialOrderBy = KnowledgeFeedOrder[urlParams.get("o") as keyof typeof KnowledgeFeedOrder];
        // Pelo acesso rápido, a ordem deve ser inicializada como "Mais Recente".
        const order = initialOrderBy ?? (isHome ? KnowledgeFeedOrder.Latest : KnowledgeFeedOrder.MostRelevant);

        setCount(null);
        setPrevPub(location.state?.publicationId ?? null);
        viewFilter.current = location.state?.filter
            ?? {
                topicDescendants: includeDescendants ?? false,
                searchText: "",
                ftsTitleAuthor: true,
                advFilters: [],
                order
            } as IFeedFilterOptions;

        feedRef.current?.reset();

        subscribe("TopicNewPublication", handleNewItem);

        return () => {
            unsubscribe("TopicNewPublication", handleNewItem);
        };
    }, [topic]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return topic != null && viewFilter.current != null && (
        <>
            <Box mt={3} mb={3}>

                <FeedTopicFilter
                    topic={topic!}
                    count={count ?? undefined}
                    filter={viewFilter.current}
                    onFilterChange={handleAdvFilterChange}
                    expandedAdvFilter={expandedAdvFilter}
                    onError={(error) => MprcRequestSnackbarErrorReport(error)}
                    onGetQuickFilterOptions={() =>
                        new Promise<QuickFilterDto[]>((resolve, reject) => {
                            api.v1.knowledgeFeed.getQuickFilterFeedTopic({ topicId: topic!.topicId })
                                .then(result => resolve(result.data))
                                .catch(e => reject(e));
                        })
                    }
                    onSelectedExpandedAdvFilter={(selected) => {
                        setExpandedAdvFilter(selected);
                        navigate(document.location.search, { replace: true, state: { expanded: selected } });
                    }}
                />
            </Box>
            <FeedContainer
                feed={
                    <FeedManager
                        feedType={FeedType.Topic}
                        otherInfo={{ feedType: FeedType.Topic }}
                        onGetData={onGetData}
                        ref={feedRef}
                        useInfiniteLoading
                        previousPublicationId={prevPub}
                        onGobackTop={() => {
                            setCount(null);
                            setPrevPub(null);
                        }}
                        onError={(error) => MprcRequestSnackbarErrorReport(error)}
                    />
                }
                moreInfo={
                    topic && showPanel
                        ? <FeedTopicRightPanel
                            key="FeedTopicRightPanel"
                            topicId={topic.topicId}
                            renderMode="default"
                            onEmptyPanel={() => setShowPanel(false)}
                        />
                        : undefined
                }
                marginRightPanel={0}
            />

            <OutsideRightPanel
                open={openRightPanel}
                onClose={() => setOpenRightPanel(false)}
                content={
                    <FeedTopicRightPanel
                        key="FeedTopicRightPanel"
                        topicId={topic!.topicId}
                        renderMode="drawer"
                        onEmptyPanel={() => setShowPanel(false)}
                    />
                }
            />

        </>
    );
};
